import React, { useCallback, useRef } from "react";
import { FiSearch } from "react-icons/fi";
import type { UserStatus, UserListFilters } from "../../../types/userInvite";

const STATUS_OPTIONS: { label: string; value: UserStatus; }[] = [
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "deactivated", label: "Deactivated" },
];

interface UserListFiltersProps {
  filters: UserListFilters;
  onChange: (filters: UserListFilters) => void;
}

export const UserListFiltersComponent: React.FC<UserListFiltersProps> = ({ filters, onChange }) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onChange({ ...filters, search: value || undefined, page: 1 });
      }, 300);
    },
    [filters, onChange]
  );

  const handleStatusChange = useCallback(
    (status: UserStatus) => {
      const current = filters.status ?? [];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      onChange({ ...filters, status: next.length > 0 ? next : undefined, page: 1 });
    },
    [filters, onChange]
  );

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
      <div className="relative flex-1 min-w-[200px]">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
        <input
          type="text"
          placeholder="Search by name or email…"
          defaultValue={filters.search ?? ""}
          onChange={handleSearchChange}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="user-search-input"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status:</span>
        {STATUS_OPTIONS.map((opt) => {
          const isSelected = (filters.status ?? []).includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleStatusChange(opt.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                isSelected
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
              }`}
              data-testid={`user-status-filter-${opt.value}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
